import React, { useState, useEffect } from 'react';
import { PlusIcon, XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import type { ProposalFormData } from '../../../types';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`;

interface ProposalTemplate {
  id: number;
  title: string;
  template_type: string;
  content: string;
  is_active: boolean;
  is_default: boolean;
  order: number;
}

interface TemplatesData {
  contractor_obligations: string[];
  client_obligations: string[];
  general_observations: string[];
}

interface ObligationsStepProps {
  formData: ProposalFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProposalFormData>>;
  errors: any;
}

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: string[];
  title: string;
  onSelect: (template: string) => void;
}

const TemplateModal: React.FC<TemplateModalProps> = ({ isOpen, onClose, templates, title, onSelect }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 max-w-3xl max-h-[75vh] overflow-y-auto m-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          {templates.map((template, index) => (
            <button
              key={index}
              type="button"
              onClick={() => onSelect(template)}
              className="w-full text-left p-2 border rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <span className="text-xs text-gray-700">{template}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ObligationsStep: React.FC<ObligationsStepProps> = ({ formData, setFormData, errors }) => {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<{ type: string, index: number } | null>(null);
  const [editText, setEditText] = useState('');
  const [templates, setTemplates] = useState<TemplatesData>({
    contractor_obligations: [],
    client_obligations: [],
    general_observations: []
  });
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Carregar templates da API
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const authTokensStr = localStorage.getItem('authTokens');
        if (!authTokensStr) {
          console.error('Usuário não autenticado');
          setLoadingTemplates(false);
          return;
        }

        const authTokens = JSON.parse(authTokensStr);
        const token = authTokens.access;

        const response = await fetch(`${API_BASE_URL}/api/v1/settings/templates/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const templatesList: ProposalTemplate[] = Array.isArray(data) ? data : (data.results || []);

          // Organizar templates por tipo e extrair apenas os conteúdos ativos
          const organized: TemplatesData = {
            contractor_obligations: templatesList
              .filter(t => t.template_type === 'contractor_obligation' && t.is_active)
              .sort((a, b) => a.order - b.order)
              .map(t => t.content),
            client_obligations: templatesList
              .filter(t => t.template_type === 'client_obligation' && t.is_active)
              .sort((a, b) => a.order - b.order)
              .map(t => t.content),
            general_observations: templatesList
              .filter(t => t.template_type === 'general_observation' && t.is_active)
              .sort((a, b) => a.order - b.order)
              .map(t => t.content)
          };

          setTemplates(organized);
        }
      } catch (err) {
        console.error('Erro ao carregar templates:', err);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, []);

  // Inicializar arrays vazios se não existirem
  useEffect(() => {
    if (!formData.contractor_obligations) {
      setFormData(prev => ({ ...prev, contractor_obligations: [] }));
    }
    if (!formData.client_obligations) {
      setFormData(prev => ({ ...prev, client_obligations: [] }));
    }
    if (!formData.general_observations) {
      setFormData(prev => ({ ...prev, general_observations: [] }));
    }
  }, [formData, setFormData]);

  const addObligation = (type: keyof Pick<ProposalFormData, 'contractor_obligations' | 'client_obligations' | 'general_observations'>, text: string) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...(prev[type] || []), text]
    }));
    setActiveModal(null);
  };

  const removeObligation = (type: keyof Pick<ProposalFormData, 'contractor_obligations' | 'client_obligations' | 'general_observations'>, index: number) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type]?.filter((_, i) => i !== index) || []
    }));
  };

  const startEdit = (type: string, index: number, currentText: string) => {
    setEditingIndex({ type, index });
    setEditText(currentText);
  };

  const saveEdit = () => {
    if (!editingIndex) return;
    
    const { type, index } = editingIndex;
    setFormData(prev => {
      const newArray = [...(prev[type as keyof ProposalFormData] as string[] || [])];
      newArray[index] = editText;
      return {
        ...prev,
        [type]: newArray
      };
    });
    
    setEditingIndex(null);
    setEditText('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const addCustomObligation = (type: keyof Pick<ProposalFormData, 'contractor_obligations' | 'client_obligations' | 'general_observations'>) => {
    const text = prompt('Digite o texto da obrigação/observação:');
    if (text && text.trim()) {
      addObligation(type, text.trim());
    }
  };

  const renderObligationSection = (
    title: string,
    type: keyof Pick<ProposalFormData, 'contractor_obligations' | 'client_obligations' | 'general_observations'>,
    templates: string[],
    colorScheme: 'blue' | 'green' | 'purple'
  ) => {
    const obligations = formData[type] || [];
    
    // Define color classes statically to ensure they're included by Tailwind
    const colorClasses = {
      blue: 'bg-blue-600 hover:bg-blue-700',
      green: 'bg-green-600 hover:bg-green-700', 
      purple: 'bg-purple-600 hover:bg-purple-700'
    };
    
    return (
      <div className="bg-white rounded border border-gray-200 p-3">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-xs font-medium text-gray-700">{title}</h4>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setActiveModal(type)}
              className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white ${colorClasses[colorScheme]}`}
            >
              <DocumentTextIcon className="h-3 w-3 mr-1" />
              Template
            </button>
            <button
              type="button"
              onClick={() => addCustomObligation(type)}
              className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-3 w-3 mr-1" />
              Personalizado
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {obligations.map((obligation, index) => (
            <div key={index} className="flex items-start gap-2 p-2 border rounded bg-gray-50">
              <div className="flex-1">
                {editingIndex?.type === type && editingIndex?.index === index ? (
                  <div className="space-y-1.5">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full p-1.5 border border-gray-300 rounded text-xs"
                      rows={2}
                    />
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={saveEdit}
                        className="px-2 py-0.5 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        Salvar
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="px-2 py-0.5 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-gray-700 leading-relaxed">{obligation}</p>
                    <button
                      type="button"
                      onClick={() => startEdit(type, index, obligation)}
                      className="text-[10px] text-blue-600 hover:text-blue-800 mt-0.5"
                    >
                      Editar
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeObligation(type, index)}
                className="text-red-500 hover:text-red-700 flex-shrink-0 mt-0.5"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}

          {obligations.length === 0 && (
            <div className="text-center py-4 text-gray-400">
              <DocumentTextIcon className="h-8 w-8 mx-auto mb-1 text-gray-300" />
              <p className="text-xs">Nenhuma {title.toLowerCase()} adicionada.</p>
              <p className="text-[10px]">Use os botões acima para adicionar.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-1">Obrigações e Observações</h3>
        <p className="text-xs text-gray-500">
          Configure as obrigações contratuais e observações gerais da proposta
        </p>
      </div>

      {loadingTemplates ? (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-xs text-gray-500 mt-1.5">Carregando templates...</p>
        </div>
      ) : (
        <>
          {renderObligationSection(
            'Obrigações da Contratada',
            'contractor_obligations',
            templates.contractor_obligations,
            'blue'
          )}

          {renderObligationSection(
            'Obrigações da Contratante',
            'client_obligations',
            templates.client_obligations,
            'green'
          )}

          {renderObligationSection(
            'Observações Gerais',
            'general_observations',
            templates.general_observations,
            'purple'
          )}
        </>
      )}

      {/* Modais de templates */}
      <TemplateModal
        isOpen={activeModal === 'contractor_obligations'}
        onClose={() => setActiveModal(null)}
        templates={templates.contractor_obligations}
        title="Selecione uma Obrigação da Contratada"
        onSelect={(template) => addObligation('contractor_obligations', template)}
      />

      <TemplateModal
        isOpen={activeModal === 'client_obligations'}
        onClose={() => setActiveModal(null)}
        templates={templates.client_obligations}
        title="Selecione uma Obrigação da Contratante"
        onSelect={(template) => addObligation('client_obligations', template)}
      />

      <TemplateModal
        isOpen={activeModal === 'general_observations'}
        onClose={() => setActiveModal(null)}
        templates={templates.general_observations}
        title="Selecione uma Observação Geral"
        onSelect={(template) => addObligation('general_observations', template)}
      />
    </div>
  );
};

export default ObligationsStep;